'use client';

import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUniversalAccess, 
  faSitemap, 
  faTimes, 
  faUndo, 
  faVolumeUp, 
  faPlay, 
  faStop, 
  faMinus, 
  faPlus, 
  faAdjust, 
  faExclamationCircle, 
  faTintSlash, 
  faAlignLeft, 
  faAlignCenter, 
  faAlignRight, 
  faAlignJustify, 
  faRulerHorizontal, 
  faMousePointer, 
  faLink, 
  faFont, 
  faImage, 
  faPauseCircle 
} from '@fortawesome/free-solid-svg-icons';

// SVG Filters for Color Blindness
const ColorBlindFilters = () => (
  <svg style={{ display: 'none' }}>
    <defs>
      <filter id="protanopia">
        <feColorMatrix type="matrix" values="0.567, 0.433, 0, 0, 0, 0.558, 0.442, 0, 0, 0, 0, 0.242, 0.758, 0, 0, 0, 0, 0, 1, 0" />
      </filter>
      <filter id="deuteranopia">
        <feColorMatrix type="matrix" values="0.625, 0.375, 0, 0, 0, 0.7, 0.3, 0, 0, 0, 0, 0.3, 0.7, 0, 0, 0, 0, 0, 1, 0" />
      </filter>
      <filter id="tritanopia">
        <feColorMatrix type="matrix" values="0.95, 0.05, 0, 0, 0, 0, 0.433, 0.567, 0, 0, 0, 0.475, 0.525, 0, 0, 0, 0, 0, 1, 0" />
      </filter>
      <filter id="achromatopsia">
        <feColorMatrix type="matrix" values="0.299, 0.587, 0.114, 0, 0, 0.299, 0.587, 0.114, 0, 0, 0.299, 0.587, 0.114, 0, 0, 0, 0, 0, 1, 0" />
      </filter>
    </defs>
  </svg>
);

export default function AccessibilityWidget() {
  const [isOpen, setIsOpen] = useState(false);
  
  // Basic State
  const [fontSize, setFontSize] = useState(100);
  const [contrastMode, setContrastMode] = useState<'normal' | 'dark' | 'light' | 'high-contrast' | 'smart'>('normal');
  const [saturation, setSaturation] = useState(100);
  
  // Advanced Visuals
  const [colorBlindness, setColorBlindness] = useState<'normal' | 'protanopia' | 'deuteranopia' | 'tritanopia' | 'achromatopsia'>('normal');
  const [textAlignment, setTextAlignment] = useState<'normal' | 'left' | 'right' | 'center' | 'justify'>('normal');
  const [zoomLevel, setZoomLevel] = useState(100); // Page Zoom

  // Tools
  const [bigCursor, setBigCursor] = useState(false);
  const [highlightLinks, setHighlightLinks] = useState(false);
  const [readableFont, setReadableFont] = useState(false);
  const [letterSpacing, setLetterSpacing] = useState<'normal' | 'wide' | 'wider'>('normal');
  const [hideImages, setHideImages] = useState(false);
  const [readingGuide, setReadingGuide] = useState(false);
  const [stopAnimations, setStopAnimations] = useState(false);
  const [screenReader, setScreenReader] = useState(false);
  
  // Headings Map
  const [headingsMapOpen, setHeadingsMapOpen] = useState(false);
  const [headers, setHeaders] = useState<{ id: string; text: string; level: number }[]>([]);

  // Reading Guide Ref
  const guideRef = useRef<HTMLDivElement>(null);

  // Scan Headers for Map
  useEffect(() => {
     if (headingsMapOpen) {
          const els = document.querySelectorAll('h1, h2, h3, h4, h5, h6');
          const headerData: any[] = [];
           els.forEach((el, index) => {
               if (!el.id) el.id = `a11y-heading-${index}`;
               headerData.push({
                   id: el.id,
                   text: (el as HTMLElement).innerText,
                   level: parseInt(el.tagName.substring(1))
               });
           });
           setHeaders(headerData);
     }
  }, [headingsMapOpen]);

  // Effect for Reading Guide
  useEffect(() => {
    if (readingGuide) {
        const handleMouseMove = (e: MouseEvent) => {
            if (guideRef.current) {
                guideRef.current.style.top = `${e.clientY}px`;
            }
        };
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [readingGuide]);

  // Effect for Screen Reader (Click to Speak)
  useEffect(() => {
      if (!screenReader) {
          window.speechSynthesis.cancel(); 
          return;
      }

      const handleElementClick = (e: MouseEvent) => {
          const widget = document.getElementById('accessibility-widget-container');
          if (widget && widget.contains(e.target as Node)) return;

          e.preventDefault();
          e.stopPropagation();

          const target = e.target as HTMLElement;
          const text = target.innerText || target.textContent;
          
          if (text && text.trim().length > 0) {
              target.classList.add('speech-highlight');
              setTimeout(() => target.classList.remove('speech-highlight'), 1000);
              window.speechSynthesis.cancel();
              const utterance = new SpeechSynthesisUtterance(text);
              utterance.lang = 'id-ID';
              utterance.rate = 1;
              window.speechSynthesis.speak(utterance);
          }
      };

      document.addEventListener('click', handleElementClick, true);
      return () => {
          document.removeEventListener('click', handleElementClick, true);
          window.speechSynthesis.cancel();
      };
  }, [screenReader]);

  // Main Style Application Effect
  useEffect(() => {
    // 1. Font Size
    document.documentElement.style.fontSize = `${fontSize}%`;

    // 2. Zoom (Scale Transform on Wrapper)
    const contentWrapper = document.getElementById('a11y-content');
    if (contentWrapper) {
        if (zoomLevel !== 100) {
            contentWrapper.style.transform = `scale(${zoomLevel / 100})`;
            contentWrapper.style.transformOrigin = 'top center';
            contentWrapper.style.overflow = 'hidden'; // Prevent scroll issues?
            // Note: Scale transform might cause scrollbar issues. 
            // Better: zoom property (non-standard but effective) or just font-size?
            // We already have fontSize. Let's stick to simple CSS zoom if supported or transform.
            // transform scale is tricky with layout. 
            // Let's use `zoom` CSS property which works well in Chrome/Edge for "Magnifier" feel.
            (contentWrapper.style as any).zoom = `${zoomLevel}%`; 
        } else {
             contentWrapper.style.transform = 'none';
             (contentWrapper.style as any).zoom = '100%';
        }
    }

    // 3. Filters (Contrast, Saturation, Color Blindness)
    let filters = [];
    if (saturation !== 100) filters.push(`saturate(${saturation}%)`);
    if (contrastMode === 'high-contrast') filters.push('contrast(150%)');
    if (colorBlindness !== 'normal') filters.push(`url(#${colorBlindness})`);

    if (contentWrapper) {
        contentWrapper.style.filter = filters.join(' ');
    }

    // 4. Injected Styles
    const styleId = 'accessibility-styles';
    let styleEl = document.getElementById(styleId);
    if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
    }

    let css = '';
    const wrapperSelector = '#a11y-content'; 

    // Contrast Modes
    if (contrastMode === 'dark') {
        css += `
            html, body { background-color: #121212 !important; }
            ${wrapperSelector} { background-color: #121212 !important; color: #fff !important; }
            ${wrapperSelector} *:not(img):not(i) { background-color: #121212 !important; color: #fff !important; border-color: #333 !important; } /* More specifc */
            ${wrapperSelector} a { color: #81b3ff !important; }
        `;
    } else if (contrastMode === 'light') {
         css += `
            html, body { background-color: #fff !important; }
            ${wrapperSelector} { background-color: #fff !important; color: #000 !important; }
            ${wrapperSelector} *:not(img):not(i) { background-color: #fff !important; color: #000 !important; border-color: #ccc !important; }
        `;
    } else if (contrastMode === 'high-contrast') {
         css += `
            html, body { background-color: black !important; }
            ${wrapperSelector} * { background-color: black !important; color: yellow !important; border-color: yellow !important; }
            ${wrapperSelector} a { color: cyan !important; text-decoration: underline !important; }
            ${wrapperSelector} img { filter: grayscale(100%) contrast(150%) !important; opacity: 0.8; }
         `;
    } else if (contrastMode === 'smart') {
         // Smart Contrast: Enhance borders and outlines
         css += `${wrapperSelector} * { outline: 1px solid rgba(0,0,0,0.5) !important; }`;
    }

    // Alignment
    if (textAlignment !== 'normal') {
        css += `${wrapperSelector} p, ${wrapperSelector} h1, ${wrapperSelector} h2, ${wrapperSelector} h3, ${wrapperSelector} h4, ${wrapperSelector} h5, ${wrapperSelector} h6, ${wrapperSelector} span, ${wrapperSelector} div { text-align: ${textAlignment} !important; }`;
    }

    // Big Cursor
    if (bigCursor) {
        css += `* { cursor: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg'  width='40' height='48' viewport='0 0 100 100' style='fill:black;font-size:24px;'><text y='50%'>👆</text></svg>") 16 0, auto !important; }`;
    }

    // Highlight Links
    if (highlightLinks) {
        css += `${wrapperSelector} a { text-decoration: underline !important; background-color: yellow !important; color: black !important; font-weight: bold !important; outline: 2px solid black !important; }`;
    }

    // Readable Font
    if (readableFont) {
        css += `* { font-family: Arial, Helvetica, sans-serif !important; word-spacing: 0.1em !important; }`;
    }

    // Letter Spacing
    if (letterSpacing === 'wide') css += `${wrapperSelector} * { letter-spacing: 0.12em !important; line-height: 1.8 !important; }`;
    else if (letterSpacing === 'wider') css += `${wrapperSelector} * { letter-spacing: 0.25em !important; line-height: 2.2 !important; }`;

    // Hide Images
    if (hideImages) {
        css += `${wrapperSelector} img, ${wrapperSelector} video, ${wrapperSelector} iframe, ${wrapperSelector} svg { opacity: 0 !important; visibility: hidden !important; } 
                ${wrapperSelector} * { background-image: none !important; }`;
    }

    // Stop Animations
    if (stopAnimations) {
        css += `* { animation: none !important; transition: none !important; scroll-behavior: auto !important; }`;
    }
    
    // Screen Reader Styles (Pulse)
    css += `
        @keyframes speech-pulse {
            0% { outline: 2px solid transparent; background-color: rgba(255, 255, 0, 0); }
            50% { outline: 2px solid #ffcc00; background-color: rgba(255, 255, 0, 0.2); }
            100% { outline: 2px solid transparent; background-color: rgba(255, 255, 0, 0); }
        }
        .speech-highlight { animation: speech-pulse 1s ease-out; }
    `;

    styleEl.innerHTML = css;

    return () => {
       // Cleanup handled by effects running again
    };
  }, [fontSize, contrastMode, saturation, colorBlindness, textAlignment, zoomLevel, bigCursor, highlightLinks, readableFont, letterSpacing, hideImages, stopAnimations, screenReader]);

  const resetAll = () => {
    setFontSize(100);
    setContrastMode('normal');
    setSaturation(100);
    setColorBlindness('normal');
    setTextAlignment('normal');
    setZoomLevel(100);
    setBigCursor(false);
    setHighlightLinks(false);
    setReadableFont(false);
    setLetterSpacing('normal');
    setHideImages(false);
    setReadingGuide(false);
    setStopAnimations(false);
    setScreenReader(false);
    setHeadingsMapOpen(false);
  };

  const FeatureBtn = ({ active, onClick, icon, label }: any) => (
    <button 
        type="button"
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClick();
        }}
        className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all duration-200 border min-h-[80px] ${
            active 
            ? 'bg-unmulGreen text-white border-unmulGreen shadow-lg scale-105' 
            : 'bg-gray-50 text-gray-600 border-gray-100 hover:bg-white hover:shadow-md hover:border-gray-200'
        }`}
    >
        <FontAwesomeIcon icon={icon} className="text-xl mb-1.5" />
        <span className="text-[10px] font-bold uppercase tracking-tight text-center leading-tight">{label}</span>
    </button>
  );

  return (
    <div id="accessibility-widget-container" className="fixed bottom-6 left-6 z-[9999] font-sans pointer-events-none">
      <ColorBlindFilters />
      
      {/* Reading Guide Overlay */}
      {readingGuide && (
          <div 
            ref={guideRef} 
            className="fixed left-0 w-full h-1 bg-yellow-400 z-[9998] pointer-events-none shadow-sm"
            style={{ mixBlendMode: 'difference' }}
          ></div>
      )}

      {/* Headings Map Sidebar */}
      <div 
        className={`fixed left-0 top-0 bottom-0 w-64 bg-white shadow-2xl z-[10000] border-r border-gray-100 transition-transform duration-300 ${headingsMapOpen ? 'translate-x-0 pointer-events-auto' : '-translate-x-full pointer-events-none'}`}
      >
        <div className="p-4 bg-unmulGreen text-white flex justify-between items-center">
            <h3 className="font-bold"><FontAwesomeIcon icon={faSitemap} className="mr-2" /> Page Structure</h3>
            <button onClick={() => setHeadingsMapOpen(false)} aria-label="Close Page Structure"><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="overflow-y-auto h-full p-4 pb-20 custom-scrollbar">
            {headers.length === 0 ? <p className="text-sm text-gray-500 italic">No headings found.</p> : (
                <ul className="space-y-2">
                    {headers.map((h, i) => (
                        <li key={i} style={{ paddingLeft: `${(h.level - 1) * 0.5}rem` }}>
                            <a 
                                href={`#${h.id}`} 
                                className="text-xs text-blue-600 hover:underline block py-1"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth' });
                                }}
                            >
                                <span className="font-mono text-gray-400 mr-2">H{h.level}</span>
                                {h.text.substring(0, 50)}{h.text.length > 50 && '...'}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
      </div>

      {/* Widget Panel */}
      <div 
        className={`bg-white rounded-2xl shadow-2xl border border-gray-100 mb-4 overflow-hidden transition-all duration-400 origin-bottom-left pointer-events-auto ${
          isOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8 pointer-events-none'
        }`}
        style={{ width: '400px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-unmulGreen to-unmulDarkGreen text-white px-5 py-4 flex justify-between items-center shrink-0">
          <div>
             <h3 className="font-bold flex items-center gap-2 text-lg">
                <FontAwesomeIcon icon={faUniversalAccess} /> Aksesibilitas
             </h3>
             <p className="text-[10px] text-white/80 uppercase tracking-wider font-semibold mt-1">Universal Access Tools</p>
          </div>
          <div className="flex gap-2">
             <button onClick={() => setHeadingsMapOpen(!headingsMapOpen)} className="text-xs bg-white/20 hover:bg-white/30 text-white px-2 py-1.5 rounded-lg transition" title="Page Structure" aria-label="Toggle Page Structure">
                <FontAwesomeIcon icon={faSitemap} />
             </button>
             <button onClick={resetAll} className="text-xs bg-white/20 hover:bg-white/30 text-white px-3 py-1.5 rounded-lg transition font-medium">
                <FontAwesomeIcon icon={faUndo} className="mr-1" /> Reset
             </button>
          </div>
        </div>
        
        {/* Scrollable Content */}
        <div className="p-5 overflow-y-auto custom-scrollbar space-y-8">
            
            {/* 0. Screen Reader */}
            <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                <div className="flex justify-between items-center mb-2">
                     <label className="text-xs font-bold text-unmulGreen uppercase tracking-wider flex items-center gap-2">
                        <FontAwesomeIcon icon={faVolumeUp} /> Screen Reader
                    </label>
                    {screenReader && <span className="text-[10px] bg-green-200 text-unmulGreen px-2 py-0.5 rounded-full font-bold animate-pulse">Active</span>}
                </div>
                <button 
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setScreenReader(!screenReader); }}
                    className={`w-full py-2.5 px-4 rounded-lg font-bold transition flex items-center justify-center gap-2 text-sm ${
                        screenReader 
                        ? 'bg-unmulGreen text-white shadow-lg shadow-green-200' 
                        : 'bg-white text-unmulGreen border border-unmulGreen/30 hover:bg-unmulGreen hover:text-white'
                    }`}
                >
                    <FontAwesomeIcon icon={screenReader ? faStop : faPlay} />
                    {screenReader ? 'Nonaktifkan Suara' : 'Aktifkan Klik-untuk-Bicara'}
                </button>
            </div>

            {/* 1. Zoom & Font */}
            <div className="space-y-4">
                 <h4 className="text-xs font-bold text-gray-400 border-b border-gray-100 pb-1 mb-2">ZOOM & FONT</h4>
                 
                 {/* Font Size */}
                 <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <span className="text-xs font-semibold text-gray-600 ml-2">Text Size</span>
                    <div className="flex gap-1">
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFontSize(Math.max(80, fontSize - 10)); }} className="w-8 h-8 bg-white rounded shadow-sm flex items-center justify-center text-gray-600 hover:text-unmulGreen" aria-label="Decrease Text Size"><FontAwesomeIcon icon={faMinus} /></button>
                        <span className="w-10 flex items-center justify-center font-bold text-sm text-unmulGreen">{fontSize}%</span>
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setFontSize(Math.min(200, fontSize + 10)); }} className="w-8 h-8 bg-white rounded shadow-sm flex items-center justify-center text-gray-600 hover:text-unmulGreen" aria-label="Increase Text Size"><FontAwesomeIcon icon={faPlus} /></button>
                    </div>
                 </div>

                 {/* Page Zoom */}
                 <div className="flex items-center justify-between bg-gray-50 p-2 rounded-lg">
                    <span className="text-xs font-semibold text-gray-600 ml-2">Page Zoom</span>
                    <div className="flex gap-1">
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setZoomLevel(Math.max(80, zoomLevel - 10)); }} className="w-8 h-8 bg-white rounded shadow-sm flex items-center justify-center text-gray-600 hover:text-unmulGreen" aria-label="Decrease Zoom"><FontAwesomeIcon icon={faMinus} /></button>
                        <span className="w-10 flex items-center justify-center font-bold text-sm text-unmulGreen">{zoomLevel}%</span>
                        <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setZoomLevel(Math.min(200, zoomLevel + 10)); }} className="w-8 h-8 bg-white rounded shadow-sm flex items-center justify-center text-gray-600 hover:text-unmulGreen" aria-label="Increase Zoom"><FontAwesomeIcon icon={faPlus} /></button>
                    </div>
                 </div>
            </div>

            {/* 2. Visual Modes */}
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 border-b border-gray-100 pb-1 mb-2">VISUAL FILTERS</h4>
                <div className="grid grid-cols-3 gap-2">
                    <FeatureBtn active={contrastMode === 'smart'} onClick={() => setContrastMode(contrastMode === 'smart' ? 'normal' : 'smart')} icon={faAdjust} label="Smart Contrast" />
                    <FeatureBtn active={contrastMode === 'high-contrast'} onClick={() => setContrastMode(contrastMode === 'high-contrast' ? 'normal' : 'high-contrast')} icon={faExclamationCircle} label="High Contrast" />
                    <FeatureBtn active={saturation === 0} onClick={() => setSaturation(saturation === 0 ? 100 : 0)} icon={faTintSlash} label="Grayscale" />
                </div>
            </div>
            
            {/* 3. Color Blindness */}
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 border-b border-gray-100 pb-1 mb-2">COLOR BLINDNESS</h4>
                <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setColorBlindness(colorBlindness === 'protanopia' ? 'normal' : 'protanopia'); }} className={`py-2 px-3 text-xs rounded-lg border transition ${colorBlindness === 'protanopia' ? 'bg-unmulGreen text-white' : 'bg-gray-50'}`}>Protanopia (Red)</button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setColorBlindness(colorBlindness === 'deuteranopia' ? 'normal' : 'deuteranopia'); }} className={`py-2 px-3 text-xs rounded-lg border transition ${colorBlindness === 'deuteranopia' ? 'bg-unmulGreen text-white' : 'bg-gray-50'}`}>Deuteranopia (Green)</button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setColorBlindness(colorBlindness === 'tritanopia' ? 'normal' : 'tritanopia'); }} className={`py-2 px-3 text-xs rounded-lg border transition ${colorBlindness === 'tritanopia' ? 'bg-unmulGreen text-white' : 'bg-gray-50'}`}>Tritanopia (Blue)</button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setColorBlindness(colorBlindness === 'achromatopsia' ? 'normal' : 'achromatopsia'); }} className={`py-2 px-3 text-xs rounded-lg border transition ${colorBlindness === 'achromatopsia' ? 'bg-unmulGreen text-white' : 'bg-gray-50'}`}>Achromatopsia (Mono)</button>
                </div>
            </div>

            {/* 4. Text Alignment */}
            <div className="space-y-4">
                 <h4 className="text-xs font-bold text-gray-400 border-b border-gray-100 pb-1 mb-2">ALIGNMENT</h4>
                 <div className="flex bg-gray-100 rounded-lg p-1">
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTextAlignment('left'); }} className={`flex-1 py-1.5 rounded transition ${textAlignment === 'left' ? 'bg-white shadow text-unmulGreen' : 'text-gray-500'}`} aria-label="Align Left"><FontAwesomeIcon icon={faAlignLeft} /></button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTextAlignment('center'); }} className={`flex-1 py-1.5 rounded transition ${textAlignment === 'center' ? 'bg-white shadow text-unmulGreen' : 'text-gray-500'}`} aria-label="Align Center"><FontAwesomeIcon icon={faAlignCenter} /></button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTextAlignment('right'); }} className={`flex-1 py-1.5 rounded transition ${textAlignment === 'right' ? 'bg-white shadow text-unmulGreen' : 'text-gray-500'}`} aria-label="Align Right"><FontAwesomeIcon icon={faAlignRight} /></button>
                    <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setTextAlignment('justify'); }} className={`flex-1 py-1.5 rounded transition ${textAlignment === 'justify' ? 'bg-white shadow text-unmulGreen' : 'text-gray-500'}`} aria-label="Justify"><FontAwesomeIcon icon={faAlignJustify} /></button>
                 </div>
            </div>

            {/* 5. Tools */}
            <div className="space-y-4">
                <h4 className="text-xs font-bold text-gray-400 border-b border-gray-100 pb-1 mb-2">READABILITY TOOLS</h4>
                <div className="grid grid-cols-3 gap-2">
                     <FeatureBtn active={readingGuide} onClick={() => setReadingGuide(!readingGuide)} icon={faRulerHorizontal} label="Reading Guide" />
                     <FeatureBtn active={bigCursor} onClick={() => setBigCursor(!bigCursor)} icon={faMousePointer} label="Big Cursor" />
                     <FeatureBtn active={highlightLinks} onClick={() => setHighlightLinks(!highlightLinks)} icon={faLink} label="Links" />
                     <FeatureBtn active={readableFont} onClick={() => setReadableFont(!readableFont)} icon={faFont} label="Readable Font" />
                     <FeatureBtn active={hideImages} onClick={() => setHideImages(!hideImages)} icon={faImage} label="Hide Images" />
                     <FeatureBtn active={stopAnimations} onClick={() => setStopAnimations(!stopAnimations)} icon={faPauseCircle} label="Stop Anim" />
                </div>
            </div>

        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 text-center">
            <p className="text-[9px] text-gray-400 uppercase tracking-widest">Web Accessibility Initiative</p>
        </div>
      </div>

      {/* Main Toggle Button */}
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsOpen(!isOpen); }}
        className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all duration-500 transform hover:scale-110 relative group pointer-events-auto ${
          isOpen ? 'bg-unmulYellow rotate-180' : 'bg-unmulGreen animate-pulse-slow'
        }`}
        aria-label="Menu Aksesibilitas"
      >
        <div className={`absolute inset-0 rounded-full bg-white opacity-20 ${isOpen ? '' : 'animate-ping'}`} style={{ animationDuration: '3s' }}></div>
        <div className="text-2xl relative z-10 text-white">
            <FontAwesomeIcon icon={isOpen ? faTimes : faUniversalAccess} className={isOpen ? 'text-unmulGreen' : 'text-white'} />
        </div>
      </button>
    </div>
  );
}
